import axios from "axios";
import { authStore } from "../stores/auth.store";
import { API_BASE_URL } from "../configs/AppConfig";
import { getErrorMessage } from "./error-handler";
import { getCsrfToken, seedCsrfToken } from "services/v2/csrfService";
import { message } from "antd";

let isRefreshing = false;
let refreshSubscribers = [];

const STATE_CHANGING_METHODS = ["post", "put", "patch", "delete"];

const onRefreshed = (error = null) => {
  refreshSubscribers.forEach((callback) => callback(error));
  refreshSubscribers = [];
};

const addRefreshSubscriber = (callback) => {
  refreshSubscribers.push(callback);
};

const baseClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
  xsrfCookieName: "XSRF-TOKEN",
  xsrfHeaderName: "X-XSRF-TOKEN",
});

baseClient.interceptors.request.use(
  async (config) => {
    const method = (config.method || "").toLowerCase();

    if (STATE_CHANGING_METHODS.includes(method)) {
      let token = getCsrfToken();

      if (!token) {
        try {
          token = await seedCsrfToken();
        } catch {
          // seedCsrfToken logs; allow request through for clearer API error
        }
      }

      if (token) {
        config.headers["X-XSRF-TOKEN"] = token;
      }
    }

    return config;
  },
  (error) => Promise.reject(error),
);

baseClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const { status } = error.response || {};
    const { reset, setAuthenticated, setLogoutBanner, isAuthenticated } =
      authStore.getState();

    if (status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          addRefreshSubscriber((err) => {
            if (err) reject(err);
            else resolve(baseClient(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const csrfToken = getCsrfToken();

      try {
        await baseClient.post(
          "/api/v2/Authentication/RefreshToken",
          {},
          {
            headers: csrfToken ? { "X-XSRF-TOKEN": csrfToken } : {},
          },
        );
        isRefreshing = false;
        onRefreshed(null);
        return baseClient(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        onRefreshed(refreshError);
        if (isAuthenticated) setLogoutBanner(true);
        setAuthenticated(false);
        reset();
        return Promise.reject(refreshError);
      }
    }

    const cleanMessage = await getErrorMessage(error);

    if (status === 504) {
      message.error("Server connection timed out. Please try again.");
    } else if (!error.response) {
      message.error("Network error. Please check your internet connection.");
    }

    const apiError = new Error(cleanMessage);
    apiError.status = status;
    apiError.data = error.response?.data;
    return Promise.reject(apiError);
  },
);

export const v1 = {
  get: (url, config) => baseClient.get(`/api/v1${url}`, config),
  post: (url, data, config) => baseClient.post(`/api/v1${url}`, data, config),
  put: (url, data, config) => baseClient.put(`/api/v1${url}`, data, config),
  patch: (url, data, config) => baseClient.patch(`/api/v1${url}`, data, config),
  delete: (url, config) => baseClient.delete(`/api/v1${url}`, config),
};

export const v2 = {
  get: (url, config) => baseClient.get(`/api/v2${url}`, config),
  post: (url, data, config) => baseClient.post(`/api/v2${url}`, data, config),
  put: (url, data, config) => baseClient.put(`/api/v2${url}`, data, config),
  patch: (url, data, config) => baseClient.patch(`/api/v2${url}`, data, config),
  delete: (url, config) => baseClient.delete(`/api/v2${url}`, config),
};

export const apiClient = v1;
